"use client";
import { useContextElement } from "@/context/Context";
import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import countriesData from "@/data/countries.json";
import { ArrowBigLeft, Warehouse } from "lucide-react";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addPaymentInfo } from "@/helpers/gtag";
import { calculateShipping, getTopTwoDepots } from "@/lib/shippingCalculator";

const CountrySelect = ({ value, onChange }) => {
	return (
		<fieldset className='box fieldset'>
			<label htmlFor='country'>Țara</label>
			<div className='select-custom'>
				<select
					required
					className='tf-select w-100'
					id='country'
					name='address[country]'
					value={value}
					onChange={(e) => onChange(e.target.value)}>
					<option value=''>---</option>
					{countriesData.map((country) => (
						<option key={country.name} value={country.name}>
							{country.name}
						</option>
					))}
				</select>
			</div>
		</fieldset>
	);
};

export default function Checkout() {
	const { cartProducts, setCartProducts } = useContextElement();
	const [cartItems, setCartItems] = useState([]);
	const [totalPrice, setTotalPrice] = useState(0);
	const [isFreeShipping, setIsFreeShipping] = useState(false);
	const [shipping, setShipping] = useState(null);
	const [allSuppliers, setAllSuppliers] = useState([]);
	const [billingType, setBillingType] = useState(false);
	const [formData, setFormData] = useState({
		country: "Romania",
	});
	const [errors, setErrors] = useState({});
	const [romaniaData, setRomaniaData] = useState(null);

	const [complianceTerms, setComplianceTerms] = useState(false);
	const [compliancePrivacy, setCompliancePrivacy] = useState(false);

	const [paymentMethod, setPaymentMethod] = useState("card");

	const [pjNames] = useState({
		"nume-firma": "Nume firmă",
		cui: "CUI",
		"nr-inregistrare": "Nr. înregistrare",
		banca: "Bancă",
		iban: "IBAN",
		judet: "Județ",
		localitate: "Localitate",
		adresa_firma: "Adresă firmă",
		adresa_livrare: "Adresă livrare",
		phone: "Număr de telefon",
		email: "Email",
	});

	// Helper function pentru a calcula prețul corect
	const getFinalPrice = (product) => {
		if (product.priceReduced && parseFloat(product.priceReduced) < parseFloat(product.price)) {
			return parseFloat(product.priceReduced);
		}
		return parseFloat(product.price);
	};

	// Helper function pentru a verifica dacă produsul e la reducere
	const isReduced = (product) => {
		return product.priceReduced && parseFloat(product.priceReduced) < parseFloat(product.price);
	};

	useEffect(() => {
		fetch("/api/depozite")
			.then((r) => r.json())
			.then((d) => setAllSuppliers((d.depots || []).map((dep) => dep.supplier)))
			.catch(() => {});
	}, []);

	// Fetch Romania data from local file
	useEffect(() => {
		fetch("/romania-regions.json")
			.then((res) => {
				if (!res.ok) throw new Error("Failed to load Romania data");
				return res.json();
			})
			.then((data) => {
				console.log("Romania data loaded:", Object.keys(data).length, "counties");
				setRomaniaData(data);
			})
			.catch((err) => {
				console.error("Error loading Romania data:", err);
				toast.error("Nu s-au putut încărca datele de localizare");
			});
	}, []);

	useEffect(() => {
		if (cartProducts && cartProducts.length > 0) {
			const validIds = cartProducts.map((item) => item.id).filter((id) => /^\d+$/.test(id));

			if (validIds.length > 0) {
				fetch(`/api/productsdatabyids?ids=${validIds.join(",")}`)
					.then((res) => res.json())
					.then((data) => {
						const itemsWithQty = data.wishlistProducts.map((product) => {
							const cartItem = cartProducts.find((item) => item.id == product.id);
							return {
								...product,
								quantity: cartItem ? cartItem.quantity : 1,
							};
						});

						setCartItems(itemsWithQty);

						// Calculăm prețul total cu priceReduced
						const newTotalPrice = data.wishlistProducts.reduce((sum, product) => {
							const cartItem = cartProducts.find((item) => item.id == product.id);
							const quantity = cartItem ? cartItem.quantity : 0;
							const price = getFinalPrice(product);
							return sum + price * quantity;
						}, 0);

						const shippingResult = calculateShipping({
							cartItems: itemsWithQty,
							paymentMethod,
							allSuppliers,
						});
						setShipping(shippingResult);
						setIsFreeShipping(shippingResult.isFreeShipping);
						setTotalPrice(newTotalPrice + shippingResult.totalShipping);
					})
					.catch((err) => console.error("Error fetching products:", err));
			} else {
				setCartItems([]);
				setTotalPrice(0);
			}
		} else {
			setCartItems([]);
			setTotalPrice(0);
		}
	}, [cartProducts, paymentMethod, allSuppliers]);

	const openLocationSelector = async () => {
		if (!romaniaData) {
			toast.error("Se încarcă datele de localizare. Încearcă din nou!");
			return;
		}

		const counties = Object.keys(romaniaData).sort();

		const { value: selectedCounty } = await Swal.fire({
			title: "Selectează județul",
			input: "select",
			inputOptions: counties.reduce((acc, county) => {
				acc[county] = county;
				return acc;
			}, {}),
			inputPlaceholder: "Alege județul",
			showCancelButton: true,
			cancelButtonText: "Anulează",
			confirmButtonText: "Următorul →",
			inputValidator: (value) => {
				if (!value) {
					return "Trebuie să selectezi un județ!";
				}
			},
			customClass: {
				confirmButton: "swal2-confirm swal2-styled",
				cancelButton: "swal2-cancel swal2-styled",
			},
		});

		if (selectedCounty) {
			const cities = romaniaData[selectedCounty]
				.map((city) => city.name)
				.filter((value, index, self) => self.indexOf(value) === index)
				.sort();

			const { value: selectedCity } = await Swal.fire({
				title: `Selectează orașul din ${selectedCounty}`,
				html: `
					<div style="margin-bottom: 15px;">
						<input 
							type="text" 
							id="city-search" 
							class="swal2-input" 
							placeholder="Caută oraș (ex: Cluj, Bucuresti)..." 
							style="margin: 0; width: 100%; font-size: 14px;"
						>
					</div>
					<select 
						id="city-select" 
						class="swal2-input" 
						size="12" 
						style="width: 100%; height: 350px; margin: 0; font-size: 14px; cursor: pointer;"
					>
						${cities.map((city) => `<option value="${city}">${city}</option>`).join("")}
					</select>
					<p style="margin-top: 10px; font-size: 12px; color: #666;">
						💡 Poți da dublu-click pe oraș pentru confirmare rapidă
					</p>
				`,
				showCancelButton: true,
				cancelButtonText: "← Înapoi",
				confirmButtonText: "Confirmă",
				width: "600px",
				didOpen: () => {
					const searchInput = document.getElementById("city-search");
					const citySelect = document.getElementById("city-select");

					searchInput.focus();

					searchInput.addEventListener("input", (e) => {
						const search = e.target.value.toLowerCase().trim();
						const options = citySelect.options;
						let firstVisible = null;

						for (let i = 0; i < options.length; i++) {
							const city = options[i].text.toLowerCase();
							const matches = city.includes(search);
							options[i].style.display = matches ? "" : "none";

							if (matches && !firstVisible) {
								firstVisible = options[i];
							}
						}

						if (firstVisible) {
							citySelect.value = firstVisible.value;
						}
					});

					citySelect.addEventListener("dblclick", () => {
						Swal.clickConfirm();
					});

					searchInput.addEventListener("keydown", (e) => {
						if (e.key === "Enter" && citySelect.value) {
							Swal.clickConfirm();
						}
					});
				},
				preConfirm: () => {
					const citySelect = document.getElementById("city-select");
					const selectedCity = citySelect.value;
					if (!selectedCity) {
						Swal.showValidationMessage("Trebuie să selectezi un oraș!");
						return false;
					}
					return selectedCity;
				},
			});

			if (selectedCity) {
				const locationString = `${selectedCounty}, ${selectedCity}`;
				setFormData((prev) => ({ ...prev, city: locationString }));

				setErrors((prev) => {
					const newErrors = { ...prev };
					delete newErrors.city;
					return newErrors;
				});

				toast.success(`Locație selectată: ${locationString}`);
			} else if (selectedCity === undefined) {
				openLocationSelector();
			}
		}
	};

	const handleInputChange = (e) => {
		setFormData((prev) => ({
			...prev,
			[e.target.id]: e.target.value,
		}));
	};

	const validateForm = () => {
		const newErrors = {};
		if (!billingType) {
			["first-name", "last-name", "country", "city", "address", "phone", "email"].forEach((field) => {
				if (!formData[field] || formData[field].trim() === "") {
					newErrors[field] = "Acest câmp este obligatoriu";
				}
			});
		} else {
			[
				"nume-firma",
				"cui",
				"nr-inregistrare",
				"banca",
				"iban",
				"judet",
				"localitate",
				"adresa_firma",
				"adresa_livrare",
				"phone",
				"email",
			].forEach((field) => {
				if (!formData[field] || formData[field].trim() === "") {
					newErrors[field] = "Acest câmp este obligatoriu";
				}
			});
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleCheckout = async () => {
		// Verificăm acordul pentru termeni și condiții
		if (!compliancePrivacy || !complianceTerms) {
			toast.error("Trebuie să acceptați termenii și condițiile și politica de confidențialitate.");
			return;
		}

		// Validăm formularul
		if (!validateForm()) {
			toast.error("Te rugăm să completezi toate câmpurile obligatorii!");
			return;
		}

		// Afișăm loader
		const loadingToastMsg = paymentMethod === "tbi"
			? "Se procesează cererea de rate TBI Bank... (poate dura până la 30 secunde)"
			: paymentMethod === "bt-direct"
			? "Se procesează cererea de rate BT Direct... (poate dura până la 30 secunde)"
			: "Se procesează comanda...";
		const loadingToast = toast.loading(loadingToastMsg);

		try {

			// Construim payload-ul pentru backend
               const payload = {
                    ...formData,
                    billingType: billingType ? 1 : 0,
                    cartProducts: cartItems,
                    cartTotal: totalPrice.toFixed(2),
                    couponCode: "",
                    paymentMethod: paymentMethod,
                    freeShipping: isFreeShipping,
                    shippingBreakdown: shipping,
               };

               const endpoint = paymentMethod === "tbi"
                    ? "/api/tbi/process-order"
                    : paymentMethod === "bt-direct"
                    ? "/api/bt-direct/process-order"
                    : "/api/checkout_order";

               // GA4: add_payment_info
               const paymentLabel = paymentMethod === "tbi" ? "TBI Rate" : paymentMethod === "bt-direct" ? "BT Direct Rate" : "Card bancar";
               addPaymentInfo(cartItems, cartProducts, paymentLabel);

			const res = await fetch(endpoint, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload),
			});

			toast.dismiss(loadingToast);

			if (!res.ok) {
				const errorData = await res.json();
				toast.error(errorData.error || "A apărut o eroare la trimiterea comenzii.");
				return;
			}

			const response = await res.json();

			if (response.url) {
				toast.loading("Se inițializează plata...");
				window.location.href = response.url;
			} else if (response.success && paymentMethod === "ramburs") {
				toast.success("Comanda a fost plasată cu succes!");
                    setCartProducts([]);
				window.location.href = `/placed-order?orderID=${response.orderID}`;
			} else {
				toast.error("Nu am primit link-ul pentru redirecționare.");
			}

		} catch (err) {
			toast.dismiss();
			console.error("Checkout error:", err);
			if (err.name === "AbortError" || err.message?.includes("timeout")) {
				toast.error("Conexiunea a durat prea mult. Te rugăm să încerci din nou.");
			} else {
				toast.error("Eroare de rețea. Te rugăm să încerci din nou.");
			}
		}
	};

	const depotDisclaimer = (() => {
		if (!shipping || !shipping.depotLines?.length) return null;
		const top2 = getTopTwoDepots(shipping.depotLines);
		if (shipping.orderSubtotal > 500 && shipping.depotCount <= 2) {
			return (
				<div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
					<p style={{ fontSize: 12, color: "#166534", margin: 0, lineHeight: 1.6 }}>
						🎉 Ai transport gratuit! Comanda conține produse din maxim 2 depozite.
					</p>
				</div>
			);
		}
		if (shipping.orderSubtotal > 500 && shipping.depotCount > 2) {
			return (
				<div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
					<p style={{ fontSize: 12, color: "#92400e", margin: 0, lineHeight: 1.6 }}>
						⚠️ Plătești transport pentru {shipping.depotCount} depozite. Adaugă mai multe produse din{" "}
						{top2.map((d, i) => (
							<span key={i}>
								<Link href={`/depozite/${d.slug}`} style={{ color: "#000", fontWeight: 700, textDecoration: "underline" }}>{d.label}</Link>
								{i < top2.length - 1 ? " și " : ""}
							</span>
						))}
						{" "}pentru a beneficia de transport gratuit.
					</p>
				</div>
			);
		}
		return (
			<div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
				<p style={{ fontSize: 12, color: "#0000", margin: 0, lineHeight: 1.6 }}>
					Adaugă mai multe produse din{" "}
					{top2.map((d, i) => (
						<span key={i}>
							<Link href={`/depozite/${d.slug}`} style={{ color: "#000", fontWeight: 700, textDecoration: "underline" }}>{d.label}</Link>
							{i < top2.length - 1 ? " și " : ""}
						</span>
					))}
					{" "}și ajunge la 500 RON pentru transport gratuit.
				</p>
			</div>
		);
	})();

	return (
		<section className='flat-spacing-11'>
			<style jsx>{`
				.price-wrapper {
					display: flex;
					align-items: center;
					gap: 6px;
					flex-wrap: wrap;
				}
				.price-original {
					text-decoration: line-through;
					color: #999;
					font-size: 13px;
				}
				.price-reduced {
					color: #ff4444;
					font-weight: 600;
					font-size: 15px;
				}
				.discount-badge-small {
					display: inline-block;
					background: linear-gradient(135deg, #ff4444 0%, #ff6b6b 100%);
					color: white;
					padding: 2px 6px;
					border-radius: 3px;
					font-size: 10px;
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 0.3px;
				}
			`}</style>
			<div className='container'>
				<div className='tf-page-cart-wrap layout-2'>
					<div className='tf-page-cart-item'>
						<h5 className='fw-5 mb_20'>Detalii de facturare</h5>
						<select
							className='mb-4 p-2 border rounded'
							value={billingType}
							onChange={(e) => setBillingType(e.target.value === "true")}>
							<option value={false}>Persoană fizică</option>
							<option value={true}>Persoană juridică</option>
						</select>

						{!billingType ? (
							<div className='form-checkout'>
								<div className='box grid-2'>
									<fieldset className='fieldset'>
										<label htmlFor='first-name'>Prenume</label>
										<input
											required
											type='text'
											id='first-name'
											onChange={handleInputChange}
										/>
										{errors["first-name"] && (
											<small style={{ color: "red" }}>{errors["first-name"]}</small>
										)}
									</fieldset>
									<fieldset className='fieldset'>
										<label htmlFor='last-name'>Nume de familie</label>
										<input
											required
											type='text'
											id='last-name'
											onChange={handleInputChange}
										/>
										{errors["last-name"] && (
											<small style={{ color: "red" }}>{errors["last-name"]}</small>
										)}
									</fieldset>
								</div>
								<CountrySelect
									value={formData.country || "Romania"}
									onChange={(val) => setFormData((prev) => ({ ...prev, country: val }))}
								/>
								{errors["country"] && (
									<small style={{ color: "red" }}>{errors["country"]}</small>
								)}

								<fieldset className='box fieldset'>
									<label htmlFor='city'>Județ, Oraș</label>
									<div style={{ position: "relative" }}>
										<input
											required
											type='text'
											id='city'
											value={formData.city || ""}
											readOnly
											placeholder='Selectează județul și orașul'
											onClick={openLocationSelector}
											style={{
												cursor: "pointer",
												paddingRight: "40px",
												backgroundColor: "#fff",
											}}
										/>
										<button
											type='button'
											onClick={openLocationSelector}
											style={{
												position: "absolute",
												right: "10px",
												top: "50%",
												transform: "translateY(-50%)",
												background: "none",
												border: "none",
												cursor: "pointer",
												fontSize: "20px",
												padding: "5px",
											}}>
											📍
										</button>
									</div>
									{errors.city && <small style={{ color: "red" }}>{errors.city}</small>}
								</fieldset>

								<fieldset className='box fieldset'>
									<label htmlFor='address'>
										Adresă ( stradă, număr, bloc, scară, etaj, apartament, interfon, etc )
									</label>
									<input required type='text' id='address' onChange={handleInputChange} />
									{errors.address && (
										<small style={{ color: "red" }}>{errors.address}</small>
									)}
								</fieldset>
								<fieldset className='box fieldset'>
									<label htmlFor='phone'>Numar de telefon</label>
									<input required type='text' id='phone' onChange={handleInputChange} />
									{errors.phone && <small style={{ color: "red" }}>{errors.phone}</small>}
								</fieldset>
								<fieldset className='box fieldset'>
									<label htmlFor='email'>Email</label>
									<input required type='email' id='email' onChange={handleInputChange} />
									{errors.email && <small style={{ color: "red" }}>{errors.email}</small>}
								</fieldset>
								<fieldset className='box fieldset'>
									<label htmlFor='note'>Alte mențiuni</label>
									<textarea name='note' id='note' onChange={handleInputChange} />
								</fieldset>
							</div>
						) : (
							<div className='form-checkout'>
								<div className='box grid-2'>
									{[
										"nume-firma",
										"cui",
										"nr-inregistrare",
										"banca",
										"iban",
										"judet",
										"localitate",
										"adresa_firma",
										"adresa_livrare",
										"phone",
										"email",
									].map((field) => (
										<fieldset key={field} className='fieldset'>
											<label htmlFor={field}>{pjNames[field]}</label>
											<input
												required
												type={field === "email" ? "email" : "text"}
												id={field}
												onChange={handleInputChange}
											/>
											{errors[field] && (
												<small style={{ color: "red" }}>{errors[field]}</small>
											)}
										</fieldset>
									))}
								</div>
								<fieldset className='box fieldset'>
									<label htmlFor='note'>Alte mențiuni</label>
									<textarea name='note' id='note' onChange={handleInputChange} />
								</fieldset>
							</div>
						)}
					</div>

					<div className='tf-page-cart-footer'>
						<div className='tf-cart-footer-inner'>
							<h5 className='fw-5 mb_20'>Comanda mea</h5>
							<div className='tf-page-cart-checkout widget-wrap-checkout'>
								<ul className='wrap-checkout-product'>
									{cartItems.map((elm, i) => (
										<li key={i} className='checkout-product-item'>
											<figure className='img-product'>
												<Image
													alt='product'
													src={elm.images?.split("|").filter(Boolean)[0]}
													width={720}
													height={1005}
												/>
											</figure>
											<div className='content'>
												<div className='info'>
													<p className='name'>
														{cartProducts.find((p) => p.id === elm.id)
															?.quantity || 1}{" "}
														x {elm.name}
													</p>
													{isReduced(elm) && (
														<span className='discount-badge-small'>
															🔥 Reducere
														</span>
													)}
												</div>
												{isReduced(elm) ? (
													<div className='price-wrapper'>
														<span className='price-original'>
															{parseFloat(elm.price).toFixed(2)} RON
														</span>
														<span className='price-reduced'>
															{parseFloat(elm.priceReduced).toFixed(2)} RON
														</span>
													</div>
												) : (
													<span className='price'>
														{parseFloat(elm.price).toFixed(2)} RON
													</span>
												)}
											</div>
										</li>
									))}
								</ul>

								{!cartProducts.length && (
									<div className='container'>
										<div className='row align-items-center mt-5 mb-5'>
											<div className='col-12 fs-18'>
												Cosul tau de cumparaturi este gol.
											</div>
											<div className='col-12 mt-3'>
												<Link
													href={`/shop-default`}
													className='tf-btn btn-fill animate-hover-btn radius-3 w-100 justify-content-center'>
													Vezi produsele
												</Link>
											</div>
										</div>
									</div>
								)}

								{/* Per-depot shipping breakdown */}
								{shipping && shipping.depotLines.map((line, i) => (
									<div key={i} className='d-flex justify-content-between line pb_10'>
										<h6 className='fw-5' style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 5 }}><Warehouse size={14} /> Transport {line.label}</h6>
										<h6 className='total fw-5' style={line.cost === 0 ? { color: "#1a7f3c", fontWeight: 700 } : {}}>
											{line.cost === 0 ? "GRATUIT 🎉" : `${line.cost} RON`}
										</h6>
									</div>
								))}
								{shipping && shipping.codFee > 0 && (
									<div className='d-flex justify-content-between line pb_10'>
										<h6 className='fw-5' style={{ fontSize: 14 }}>Taxă ramburs</h6>
										<h6 className='total fw-5'>{shipping.codFee} RON</h6>
									</div>
								)}
								{shipping && shipping.depotLines.length > 1 && (
									<div className='d-flex justify-content-between line pb_20' style={{ borderTop: "1px solid #eee", paddingTop: 6 }}>
										<h6 className='fw-5'>Total transport</h6>
										<h6 className='total fw-5' style={shipping.isFreeShipping && !shipping.codFee ? { color: "#1a7f3c", fontWeight: 700 } : {}}>
											{shipping.totalShipping === 0 ? "GRATUIT 🎉" : `${shipping.totalShipping} RON`}
										</h6>
									</div>
								)}

								{/* Disclaimer depozite */}
								{depotDisclaimer}

								<div className='d-flex justify-content-between line pb_20'>
									<h6 className='fw-5'>Subtotal</h6>
									<h6 className='total fw-5'>{totalPrice.toFixed(2)} RON<br/><small style={{fontSize: 10}}>TVA inclus(21%)</small></h6>
								</div>

                                        <div className="payment-methods mt_20 mb_20">
                                            <h6 className="fw-5 mb_10">Metodă de plată</h6>
                                            <div className="d-flex flex-column gap-2">
                                                <label className={`checkbox-container text_black-2 p-3 border rounded ${paymentMethod === 'card' ? 'bg-light border-primary' : ''}`} style={{cursor: 'pointer'}}>
                                                    <input 
                                                        type="radio" 
                                                        name="payment" 
                                                        value="card" 
                                                        checked={paymentMethod === 'card'} 
                                                        onChange={() => setPaymentMethod('card')}
                                                        style={{display: 'none'}}
                                                    />
                                                    <span className="d-flex align-items-center justify-content-between w-100">
                                                        <span>💳 Card bancar (Stripe)</span>
                                                        {paymentMethod === 'card' && <span className="text-primary">✓</span>}
                                                    </span>
                                                </label>
                                                <label className={`checkbox-container text_black-2 p-3 border rounded ${paymentMethod === 'tbi' ? 'bg-light border-danger' : ''}`} style={{cursor: 'pointer'}}>
                                                    <input 
                                                        type="radio" 
                                                        name="payment" 
                                                        value="tbi" 
                                                        checked={paymentMethod === 'tbi'} 
                                                        onChange={() => setPaymentMethod('tbi')}
                                                        style={{display: 'none'}}
                                                    />
                                                    <span className="d-flex align-items-center justify-content-between w-100">
                                                        <span>🏦 Rate prin TBI Bank</span>
                                                        {paymentMethod === 'tbi' && <span className="text-danger">✓</span>}
                                                    </span>
                                                </label>
                                                <label className={`checkbox-container text_black-2 p-3 border rounded ${paymentMethod === 'bt-direct' ? 'bg-light border-warning' : ''}`} style={{cursor: 'pointer'}}>
                                                    <input
                                                        type="radio"
                                                        name="payment"
                                                        value="bt-direct"
                                                        checked={paymentMethod === 'bt-direct'}
                                                        onChange={() => setPaymentMethod('bt-direct')}
                                                        style={{display: 'none'}}
                                                    />
                                                    <span className="d-flex align-items-center justify-content-between w-100">
                                                        <span>🏦 Rate prin BT Direct</span>
                                                        {paymentMethod === 'bt-direct' && <span className="text-warning">✓</span>}
                                                    </span>
                                                </label>
                                                <label className={`checkbox-container text_black-2 p-3 border rounded ${paymentMethod === 'ramburs' ? 'bg-light border-success' : ''}`} style={{cursor: 'pointer'}}>
                                                    <input
                                                        type="radio"
                                                        name="payment"
                                                        value="ramburs"
                                                        checked={paymentMethod === 'ramburs'}
                                                        onChange={() => setPaymentMethod('ramburs')}
                                                        style={{display: 'none'}}
                                                    />
                                                    <span className="d-flex align-items-center justify-content-between w-100">
                                                        <span>🚚 Plată la livrare (Ramburs)</span>
                                                        {paymentMethod === 'ramburs' && <span className="text-success">✓</span>}
                                                    </span>
                                                </label>
                                            </div>
                                        </div>

								<button
									type='button'
									className='tf-btn radius-3 btn-fill btn-icon animate-hover-btn justify-content-center'
									onClick={handleCheckout}>
									Către plată
								</button>

								<div className='mb_15 d-flex align-items-center'>
									<label className='checkbox-container text_black-2'>
										<input
											type='checkbox'
											onChange={() => setCompliancePrivacy(!compliancePrivacy)}
											checked={compliancePrivacy}
										/>
										<span className='checkmark' />
										Sunt de acord cu{" "}
										<a href='/politica-de-confidentialitate'>
											Politica de Confidențialitate
										</a>
									</label>
								</div>
								<div className='mb_15 d-flex align-items-center'>
									<label className='checkbox-container text_black-2'>
										<input
											type='checkbox'
											onChange={() => setComplianceTerms(!complianceTerms)}
											checked={complianceTerms}
										/>
										<span className='checkmark' />
										Sunt de acord cu{" "}
										<a href='/termeni-si-conditii'>Termenii și Condițiile</a>
									</label>
								</div>

								<button
									type='button'
									onClick={() => (window.location.href = "/cos-de-cumparaturi")}
									className='tf-btn radius-3 btn-secondary btn-icon animate-hover-btn justify-content-center'>
									<ArrowBigLeft size={24} /> Vezi coșul de cumpărături
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
